import { browser } from '$app/environment';
import { UAParser } from 'ua-parser-js';
import { Crawlers } from 'ua-parser-js/extensions';
import { isBot } from 'ua-parser-js/helpers';

/**
 * Check if the current device is a mobile browser.
 */
export const isMobileBrowser = (): boolean => {
  if (!browser) return false;

  const parser = new UAParser();
  const deviceType = parser.getDevice().type; // Returns 'mobile', 'tablet' or undefined

  return deviceType === 'mobile' || deviceType === 'tablet';
};

/**
 * Set a cookie in the browser.
 * @param {string} name - Cookie name.
 * @param {string} value - Cookie value.
 * @param {number} hours - Expiration time in hours.
 */
export const setCookie = (name: string, value: string, hours: number) => {
  let expires = '';
  if (hours) {
    const date = new Date();
    date.setTime(date.getTime() + hours * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

/**
 * Get a cookie by name.
 * @param {string} name - Cookie name.
 * @returns {string | null} - Cookie value or null if not found.
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase a cookie by setting an expired date.
 * @param {string} name - Cookie name.
 */
export const eraseCookie = (name: string) => {
  setCookie(name, '', -365);
};

/**
 * Get browser and version information.
 * @returns {Object} - The browser name and version.
 */
export const getBrowserInfo = () => {
  if (!browser) return { browserName: '', version: -1 };

  const parser = new UAParser();
  const browserInfo = parser.getBrowser();

  return {
    browserName: browserInfo.name || '',
    version: parseInt(browserInfo.version || '-1', 10),
  };
};

/**
 * Detect if the user agent is a known bot.
 * @returns {boolean} - True if the user agent is a bot.
 */
export const detectRobot = (): boolean => {
  if (!browser) return false;

  const parser = new UAParser();
  const ua = parser.getUA();
  const browserName = parser.getBrowser().name || '';

  // Check using isBot() helper from UAParser.js
  if (isBot(ua)) return true;

  // Check against known Crawlers list
  return Object.keys(Crawlers).some(botName => browserName.includes(botName));
};

/**
 * Get device information (OS, device type, browser).
 * @returns {Object} - Device and browser details.
 */
export const getDeviceInfo = () => {
  if (!browser) return {};

  const parser = new UAParser();
  return {
    device: parser.getDevice().model || 'Unknown',
    deviceType: parser.getDevice().type || 'Unknown',
    os: parser.getOS().name || 'Unknown',
    osVersion: parser.getOS().version || 'Unknown',
    browser: parser.getBrowser().name || 'Unknown',
    browserVersion: parser.getBrowser().version || 'Unknown',
  };
};

/**
 * Detect if the browser is running inside a WebView.
 * Supports iOS (Safari WebView) and Android (Chrome WebView).
 * @returns {boolean} - True if running inside a WebView.
 */
export function isWebView(): boolean {
  if (!browser) return false;

  const ua = window.navigator.userAgent || '';
  const parser = new UAParser(ua);
  const browserName = parser.getBrowser().name || '';
  const osName = parser.getOS().name || '';

  // Detect social media in-app browsers
  const isInAppBrowser = ['Instagram', 'Facebook', 'TikTok', 'LinkedIn', 'Twitter'].includes(browserName);

  // Detect iOS WebView (Safari WebView)
  const isIosWebView = osName === 'iOS' && !ua.includes('Safari');

  // Detect Android WebView (Chrome WebView)
  const isAndroidWebView = osName === 'Android' && (ua.includes('; wv') || ua.includes('Version/'));

  return isInAppBrowser || isIosWebView || isAndroidWebView;
}